import { memo, useState } from "react";
import { useMediaQuery } from "react-responsive";

import type { Swiper as SwiperType } from "swiper";
import { A11y, Navigation, Thumbs, Zoom } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { newrelic } from "@jti/fe-clients";
import type { ProductGalleryAsset } from "@jti/fe-common";
import { BREAKPOINT_SMALL_LARGE } from "@jti/fe-common";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import "swiper/css/zoom";

import { ProductGalleryImage } from "./components/ProductGalleryImage/ProductGalleryImage";
import { ProductGalleryThumbnailImage } from "./components/ProductGalleryThumbnailImage/ProductGalleryThumbnailImage";
import { ProductGalleryVideo } from "./components/ProductGalleryVideo/ProductGalleryVideo";
import ArrowIcon from "./icons/arrow-right.svg";

import "./styles.scss";

const MAX_SLIDES_PER_VIEW = 4;

export const ProductCarousel: React.FC<{
  items: ProductGalleryAsset[];
  onChange?: (index: number) => void;
  seekLabel?: string;
  volumeLabel?: string;
  playLabel?: string;
  replayLabel?: string;
  muteLabel?: string;
  soundOnLabel?: string;
  showFullscreenLabel?: string;
  previousSlideLabel?: string;
  nextSlideLabel?: string;
}> = memo(
  ({
    items,
    onChange,
    seekLabel,
    volumeLabel,
    playLabel,
    replayLabel,
    muteLabel,
    soundOnLabel,
    showFullscreenLabel,
    previousSlideLabel,
    nextSlideLabel,
  }) => {
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
    const slideCount = items.length;
    const isTablet = useMediaQuery({ maxWidth: BREAKPOINT_SMALL_LARGE });
    const { __ } = useTranslations();

    const handleSlideChange = (swiper: SwiperType) => {
      const newIndex = swiper.activeIndex;
      onChange?.(newIndex);
    };

    if (!Array.isArray(items)) {
      const errorMessage = "Carousel invalid asset format: assets should be an array.";
      console.error(errorMessage);

      const errorObject = new Error(errorMessage);
      newrelic.noticeError(errorObject);

      return null;
    }

    return (
      <>
        <Swiper
          modules={[Navigation, A11y, Thumbs, Zoom]}
          spaceBetween={10}
          zoom={{
            maxRatio: 1.5,
            minRatio: 1,
          }}
          observeParents={true}
          className={"carousel__galleryTop swiper-container"}
          navigation={{
            prevEl: ".carousel__navPrev",
            nextEl: ".carousel__navNext",
          }}
          thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
          onSlideChange={handleSlideChange}
        >
          {items.map((item, index) => (
            <SwiperSlide key={item.path}>
              <div className="swiper-zoom-container">
                {item.type === "image" && (
                  <div className="carousel__imageWrapper">
                    <ProductGalleryImage item={item} idx={index} />{" "}
                  </div>
                )}
                {item.type === "video" && (
                  <div className="carousel__videoWrapper">
                    <ProductGalleryVideo
                      item={item}
                      idx={index}
                      seekLabel={seekLabel}
                      volumeLabel={volumeLabel}
                      playLabel={playLabel}
                      replayLabel={replayLabel}
                      muteLabel={muteLabel}
                      soundOnLabel={soundOnLabel}
                      showFullscreenLabel={showFullscreenLabel}
                    />
                  </div>
                )}
              </div>
            </SwiperSlide>
          ))}

          <div className="carousel__navigationWrapper">
            <button type="button" className="carousel__navPrev" aria-label={previousSlideLabel}>
              <ArrowIcon aria-hidden="true" />
            </button>
            <button type="button" className="carousel__navNext" aria-label={nextSlideLabel}>
              <ArrowIcon aria-hidden="true" />
            </button>
          </div>
        </Swiper>
        <Swiper
          observer={true}
          observeParents={true}
          modules={[Thumbs]}
          watchSlidesProgress
          onSwiper={(swiper) => setThumbsSwiper(swiper)}
          spaceBetween={isTablet ? 16 : 20}
          className={"carousel__galleryThumbs swiper-container"}
          slidesPerView={slideCount <= MAX_SLIDES_PER_VIEW ? slideCount : MAX_SLIDES_PER_VIEW}
          freeMode={true}
          slideToClickedSlide={true}
        >
          {items.map((item, index) => (
            <SwiperSlide key={item.path}>
              <div className="carousel__thumbnail">
                <ProductGalleryThumbnailImage item={item} idx={index} />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </>
    );
  }
);
